import { useEffect, useState } from "react";
import {
  HistoricPatternScore,
  HistoricScaleScore,
  Nature,
  PatternScoreHistory,
  ScaleScoreHistory,
} from "@dimensional-engineering/dimensional-models";
import ReactECharts from "echarts-for-react";
import { DM_Sans, Roboto_Mono } from "next/font/google";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

import styles from "./HistoricalTrackingChart.module.css";

import {
  createDateArrayFromTimestampToNow,
  createOneYearDateArrayForChartOptions,
  createSixMonthDateArrayForChartOptions,
  createThreeMonthDateArrayForChartOptions,
  createThreeYearDateArrayForChartOptions,
  plotScoresForDaysList,
} from "./utils";
import { DataPoint } from "@/models/sharedModels";
import {
  checkIfPercentileAvailable,
  getUserPatternScore,
  getUserScaleScore,
} from "@/components/traits/utils";
import { natureData } from "@/assets/traitInfo/natureData";
import { useAlertContext } from "@/context/AlertContext";
import Alert from "../Alerts/Alert";
import ThemedButton from "../ThemedButton/ThemedButton";
import { useModalContext } from "@/context/ModalContext";
import ConversionModal from "@/components/premium/ConversionModal/ConversionModal";

type Props = {
  type: "element" | "pattern";
  scoreHistory: ScaleScoreHistory | PatternScoreHistory;
  slug: string;
  nature: Nature | null | undefined;
  isPremium: boolean | null | undefined;
  percentScore?: number | null | undefined;
  handleSwipe: (value: boolean) => void;
  dimensionSlugSource?: string | null | undefined;
  isLongFormGenerated?: boolean | null | undefined;
};

const font = Roboto_Mono({ subsets: ["latin"] });

type Selector = "PERCENTILE" | "RAW";

type Timespan = "3M" | "6M" | "1Y" | "3Y" | "MAX";

export default function HistoricalTrackingChart(props: Props) {
  const [selector, setSelector] = useState<Selector>("PERCENTILE");
  const [timeSpan, setTimeSpan] = useState<Timespan>("1Y");

  const isUserPremium = useSelector((state: RootState) => state.user.isPremium);

  const { setAlertComponent } = useAlertContext();

  const hasPercentileScore = checkIfPercentileAvailable(
    props.type,
    props.slug,
    props.dimensionSlugSource,
    props.isLongFormGenerated
  );

  useEffect(() => {
    if (!hasPercentileScore) {
      setSelector("RAW");
    }
  }, [hasPercentileScore]);

  const patternScorePercentileHistory = props.scoreHistory.historicScores?.map(
    (score) => {
      const patternScore = score as HistoricPatternScore;
      return {
        ...patternScore,
        percentScore: getUserPatternScore(
          score.percentScore,
          props.slug,
          props.isLongFormGenerated
        ),
      } as HistoricPatternScore;
    }
  );

  const elementScorePercentileHistory = props.scoreHistory.historicScores?.map(
    (score) => {
      const elementScore = score as HistoricScaleScore;
      return {
        ...elementScore,
        percentScore: getUserScaleScore(
          score.percentScore,
          props.slug,
          props.dimensionSlugSource
        ),
      } as HistoricScaleScore;
    }
  );

  const { xData, yData } = useGetOptionsParameters({
    scoreHistory: props.scoreHistory,
    selector: selector,
    timeSpan: timeSpan,
    persentileScoreHistory:
      props.type === "element"
        ? { historicScores: elementScorePercentileHistory }
        : { historicScores: patternScorePercentileHistory },
  });

  const score =
    props.type === "element"
      ? getUserScaleScore(
          props.percentScore,
          props.slug,
          props.dimensionSlugSource
        )
      : getUserPatternScore(
          props.percentScore,
          props.slug,
          props.isLongFormGenerated
        );

  const natureColor =
    natureData.find((n) => n.slug === props.nature)?.color ?? "#2cb0ff";

  const option = {
    grid: {
      left: 5,
      top: 5,
      right: 5,
      bottom: 5,
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "#55504F",
      borderRadius: 7,
      borderWidth: 0,
      position: function () {
        return ["60%", "-35%"]; // Top-right (X%, Y%)
      },
      formatter: function (params: any) {
        params = params[0];
        const timestamp = Number(params.name);
        if (isNaN(timestamp)) return "Invalid Date";

        const date = new Date(timestamp);
        const formattedDate = date.toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        });
        if (params.value[1] === undefined) {
          return;
        }

        return `
        <div class="custom-tooltip">
          <div class="tooltip-value">${formattedDate}:</div>
          <div class="tooltip-value"><span class="tooltip-value-span">${params.value[1]}</span>/100</div>
        </div>
      `;
      },
      axisPointer: {
        type: "shadow",
        snap: true,
      },
    },
    xAxis: {
      type: "time",
      boundaryGap: false,
      data: xData,
      show: false,
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: "value",
      show: false,
      splitLine: {
        show: false,
      },
      max: 100,
    },
    series: [
      {
        data: yData,
        type: "line",
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: natureColor ?? "grey" },
              { offset: 1, color: "rgba(255, 160, 185, 0)" },
            ],
          },
        },
        lineStyle: {
          color: natureColor ?? "grey",
          width: 1,
        },
        symbol: "none",
        animationDurationUpdate: 0, // Disable update animations
        emphasis: {
          focus: "none",
          itemStyle: {
            opacity: 1,
          },
        },
      },
    ],
  };

  return (
    <section
      onTouchStart={() => {
        props.handleSwipe(false);
      }}
      onTouchEnd={() => {
        props.handleSwipe(true);
      }}
      onMouseDown={() => {
        props.handleSwipe(false);
      }}
      onMouseUp={() => {
        props.handleSwipe(true);
      }}
      className={styles.main}
    >
      <h2 className={styles.title}>Score history</h2>

      <div className={styles.selectorDiv}>
        <p className={styles.scoreTypeText}>Score type:</p>
        <p
          className={styles.selectorText}
          onClick={() => {
            if (!hasPercentileScore) {
              return setAlertComponent(
                <Alert
                  elementName="Alert"
                  type="warning"
                  message="Percentiles not yet available."
                />,
                3000
              );
            }
            setSelector("PERCENTILE");
          }}
          style={
            selector === "PERCENTILE"
              ? { color: "#FFFFF2", textDecoration: "underline" }
              : {}
          }
        >
          PERCENTILE
        </p>
        <p
          className={styles.selectorText}
          onClick={() => {
            setSelector("RAW");
          }}
          style={
            selector === "RAW"
              ? { color: "#FFFFF2", textDecoration: "underline" }
              : {}
          }
        >
          RAW
        </p>
      </div>

      <div className={styles.scoreDiv}>
        <h1 className={styles.scoreTextLarge}>
          {score != null ? (score * 100).toFixed(0) : "??"}
          <span className={styles.scoreTextSmall}>/100</span>
        </h1>
        <p className={`${styles.todaysScoreText} ${font.className}`}>
          {"Today's score"}
        </p>
      </div>

      {props.isPremium && !isUserPremium ? (
        <HistoricalTrackingChartPremiumGate />
      ) : (
        <ReactECharts
          style={{
            height: "100%",
            width: "100%",
            minHeight: "125px",
            minWidth: "100%",
            marginTop: "20px",
          }}
          option={option}
          notMerge={true}
          lazyUpdate={true}
          opts={{
            renderer: "svg",
            width: "auto",
          }}
        />
      )}

      <div className={styles.timeSpaneWrapper}>
        <div className={styles.timeSpaneDiv}>
          <p
            style={timeSpan === "3M" ? { textDecoration: "underline" } : {}}
            onClick={() => setTimeSpan("3M")}
            className={styles.timeSpaneText}
          >
            3M
          </p>
          <p
            style={timeSpan === "6M" ? { textDecoration: "underline" } : {}}
            onClick={() => setTimeSpan("6M")}
            className={styles.timeSpaneText}
          >
            6M
          </p>
          <p
            style={timeSpan === "1Y" ? { textDecoration: "underline" } : {}}
            onClick={() => setTimeSpan("1Y")}
            className={styles.timeSpaneText}
          >
            1Y
          </p>
          <p
            style={timeSpan === "3Y" ? { textDecoration: "underline" } : {}}
            onClick={() => setTimeSpan("3Y")}
            className={styles.timeSpaneText}
          >
            3Y
          </p>
          <p
            style={timeSpan === "MAX" ? { textDecoration: "underline" } : {}}
            onClick={() => setTimeSpan("MAX")}
            className={styles.timeSpaneText}
          >
            MAX
          </p>
        </div>
      </div>
    </section>
  );
}

type OptionProps = {
  selector: Selector;
  timeSpan: Timespan;
  scoreHistory: ScaleScoreHistory | PatternScoreHistory;
  persentileScoreHistory: ScaleScoreHistory | PatternScoreHistory;
};

function useGetOptionsParameters(props: OptionProps) {
  const [xData, setXData] = useState<number[]>();
  const [yData, setYData] = useState<DataPoint[]>();

  const userCreatedOn = useSelector(
    (state: RootState) => state.user.memberProfile?.signUpDate
  );

  useEffect(() => {
    if (props.timeSpan === "3M") {
      const xAxisArray = createThreeMonthDateArrayForChartOptions();

      const yAxisArray = plotScoresForDaysList(
        props.selector === "PERCENTILE"
          ? props.persentileScoreHistory
          : props.scoreHistory,
        xAxisArray
      );
      setXData(xAxisArray);
      setYData(yAxisArray);
    } else if (props.timeSpan === "6M") {
      const xAxisArray = createSixMonthDateArrayForChartOptions();
      const yAxisArray = plotScoresForDaysList(
        props.selector === "PERCENTILE"
          ? props.persentileScoreHistory
          : props.scoreHistory,
        xAxisArray
      );
      setXData(xAxisArray);
      setYData(yAxisArray);
    } else if (props.timeSpan === "1Y") {
      const xAxisArray = createOneYearDateArrayForChartOptions();
      const yAxisArray = plotScoresForDaysList(
        props.selector === "PERCENTILE"
          ? props.persentileScoreHistory
          : props.scoreHistory,
        xAxisArray
      );
      setXData(xAxisArray);
      setYData(yAxisArray);
    } else if (props.timeSpan === "3Y") {
      const xAxisArray = createThreeYearDateArrayForChartOptions();
      const yAxisArray = plotScoresForDaysList(
        props.selector === "PERCENTILE"
          ? props.persentileScoreHistory
          : props.scoreHistory,
        xAxisArray
      );
      setXData(xAxisArray);
      setYData(yAxisArray);
    } else if (props.timeSpan === "MAX") {
      if (userCreatedOn) {
        const xAxisArray = createDateArrayFromTimestampToNow(userCreatedOn);
        const yAxisArray = plotScoresForDaysList(
          props.selector === "PERCENTILE"
            ? props.persentileScoreHistory
            : props.scoreHistory,
          xAxisArray
        );
        setXData(xAxisArray);
        setYData(yAxisArray);
      }
    }
  }, [props.scoreHistory, props.selector, props.timeSpan, props.selector]);

  return { xData, yData };
}

const sansFont = DM_Sans({ weight: "500", subsets: ["latin"] });

export function HistoricalTrackingChartPremiumGate() {
  const { setModalComponent } = useModalContext();
  return (
    <div className={styles.premiumGateParent}>
      <div className={styles.premiumGateWrapper}>
        <p className={styles.exclusiveTo}>Score exclusive to</p>
        <h1 className={`${styles.titleGate} ${sansFont.className}`}>
          Dimensional <span className={styles.plus}>Plus</span>
        </h1>
        <ThemedButton
          onClick={() => {
            setModalComponent(<ConversionModal initialSlide={1} />);
          }}
          title="Learn More"
          style={{ marginTop: "10px" }}
        />
      </div>
    </div>
  );
}
